import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Html, OrbitControls } from '@react-three/drei';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import * as THREE from 'three';
import { generateFurnitureParts } from '../geometry/furniture';
import { previewWardrobe } from '../geometry/previewWardrobe';
import { createPanelGeometry, useMaterialTexture } from '../renderer/Viewport3D';
import type { FurnitureObject, Material, PanelPart } from '../types';

function PreviewPanel({ panel, materials, showDoorNumbers }: { panel: PanelPart; materials: Material[]; showDoorNumbers: boolean }) {
  const material = materials.find((entry) => entry.id === panel.materialId) ?? materials[0];
  const geometry = useMemo(() => createPanelGeometry(panel), [panel]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  if (!material) return null;
  return <PreviewPanelSurface panel={panel} material={material} geometry={geometry} showDoorNumbers={showDoorNumbers} />;
}

function PreviewPanelSurface({ panel, material, geometry, showDoorNumbers }: { panel: PanelPart; material: Material; geometry: THREE.BufferGeometry; showDoorNumbers: boolean }) {
  const texture = useMaterialTexture(material, panel);
  return <mesh geometry={geometry} position={panel.position.map((value) => value / 1000) as [number, number, number]} rotation={panel.rotation} castShadow receiveShadow>
    <meshStandardMaterial color={material.color} map={material.textureUrl || material.textureAssetId ? texture : null} roughness={material.roughness} metalness={material.metalness} side={THREE.DoubleSide} />
    <lineSegments><edgesGeometry args={[geometry]} /><lineBasicMaterial color="#34443d" transparent opacity={.5} /></lineSegments>
    {showDoorNumbers && panel.type === 'door' && <Html position={[0, 0, panel.size[2] / 2000 + .015]} center style={{ pointerEvents: 'none' }}><span className="preview-door-number">{Number(panel.name.match(/\d+$/)?.[0] ?? 1)}</span></Html>}
  </mesh>;
}

function PreviewCamera({ item, reset }: { item: FurnitureObject; reset: number }) {
  const { camera } = useThree();
  const controls = useRef<any>(null);
  const targetHeight = Math.max(item.slope?.leftHeight ?? item.height, item.slope?.rightHeight ?? item.height) / 2000;
  const target = useMemo(() => new THREE.Vector3(0, targetHeight, 0), [targetHeight]);
  useEffect(() => {
    const span = Math.max(item.width, item.height, item.depth) / 1000;
    const distance = Math.max(2.7, span * 1.9);
    camera.position.set(distance * .7, targetHeight + distance * .42, distance);
    camera.lookAt(target);
    camera.updateProjectionMatrix();
    controls.current?.target.copy(target);
    controls.current?.update();
  }, [camera, item.width, item.height, item.depth, target, reset]);
  return <OrbitControls ref={controls} target={target.toArray()} minDistance={.8} maxDistance={50} enableDamping dampingFactor={.08} mouseButtons={{ LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.ROTATE, RIGHT: THREE.MOUSE.PAN }} />;
}

export function WardrobePreview({ draft, materials, showDoorNumbers = false }: { draft: FurnitureObject; materials: Material[]; showDoorNumbers?: boolean }) {
  const { t } = useTranslation();
  const [showDoors, setShowDoors] = useState(true);
  const [reset, setReset] = useState(0);
  const item = useMemo(() => previewWardrobe(draft), [draft]);
  const parts = useMemo(() => generateFurnitureParts(item), [item]);
  return <aside className="wizard-preview" aria-label={t('generator.preview')}>
    <div className="wizard-preview-header"><div><strong>{t('generator.preview')}</strong><span>{item.width} × {Math.max(item.slope?.leftHeight ?? item.height, item.slope?.rightHeight ?? item.height)} × {item.depth} mm</span></div><button type="button" aria-label={t('generator.resetView')} title={t('generator.resetView')} onClick={() => setReset((value) => value + 1)}><RotateCcw size={16} /></button></div>
    <div className="wizard-preview-canvas" role="img" aria-label={t('generator.preview')}>
      <Canvas shadows camera={{ fov: 37, near: .05, far: 100, position: [4, 3, 5] }} gl={{ antialias: true }}>
        <color attach="background" args={['#19221d']} />
        <ambientLight intensity={2.2} />
        <directionalLight position={[3, 7, 6]} intensity={2.5} castShadow shadow-mapSize={[1024, 1024]} />
        <directionalLight position={[-4, 3, -3]} intensity={.8} />
        <gridHelper args={[20, 20, '#34483a', '#28352d']} position={[0, -.003, 0]} />
        {parts.filter((part) => showDoors || part.type !== 'door').map((panel) => <PreviewPanel key={panel.id} panel={panel} materials={materials} showDoorNumbers={showDoorNumbers} />)}
        <PreviewCamera item={item} reset={reset} />
      </Canvas>
    </div>
    <div className="wizard-preview-footer"><label><input type="checkbox" checked={showDoors} onChange={(event) => setShowDoors(event.target.checked)} disabled={!item.doors.enabled || item.doors.count === 0} />{t('generator.showDoors')}</label><span>{t('generator.previewHint')}</span></div>
  </aside>;
}
